<template>
  <div class="product-management-2023-page">
    <hr class="separator" style="margin-top: 1px"/>

    <div>
      <div class="custom-text">
        <span class="user-manage">23年后固件管理</span>
      </div>
      <hr class="separator"/>
    </div>
    
    <div class="content-container">
      <!-- 操作区域 -->
      <div class="operation-area">
        <!-- 导入按钮 -->
        <el-button type="primary" @click="handleImport">
          <el-icon><Upload /></el-icon>
          {{ $t('SerialNumberUpgrade.import') }}
        </el-button>
      </div>

      <!-- 搜索区域 -->
      <div class="search-area">
        <el-card class="search-card" shadow="hover">
          <el-form :inline="true" :model="searchForm" class="search-form">
            <!-- 版本号搜索 -->
            <el-form-item label="版本号">
              <el-input 
                v-model="searchForm.version" 
                placeholder="请输入版本号"
                clearable
                @keyup.enter="handleSearch"
              />
            </el-form-item>
            
            <!-- 组织ID搜索 -->
            <el-form-item label="组织ID">
              <el-input 
                v-model="searchForm.organization_id" 
                placeholder="请输入组织ID"
                clearable
                @keyup.enter="handleSearch"
              />
            </el-form-item>
            
            <!-- 操作按钮 -->
            <el-form-item>
              <el-button type="primary" @click="handleSearch">
                <el-icon><Search /></el-icon>
                搜索
              </el-button>
              <el-button @click="handleReset">
                <el-icon><RefreshRight /></el-icon>
                重置
              </el-button>
            </el-form-item>
          </el-form>
        </el-card>
      </div>

      <!-- 列表展示区域 -->
      <div class="table-area">
        <el-card class="table-card" shadow="hover">
          <!-- 表格 -->
          <el-table 
            v-loading="tableLoading" 
            :data="firmwareList" 
            style="width: 100%"
            stripe
            border
            @empty="handleEmpty"
          >
            <!-- 空数据插槽 -->
            <template #empty>
              <div style="padding: 40px 0; text-align: center;">
                <el-empty 
                  description="暂无固件版本数据"
                  image-size="100"
                >
                  <el-button type="primary" @click="fetchFirmwareList">
                    <el-icon><RefreshRight /></el-icon>
                    刷新数据
                  </el-button>
                </el-empty>
              </div>
            </template>
            <!-- ID -->
            <el-table-column 
              prop="id" 
              label="ID"
              width="80"
              sortable
            >
              <template #default="scope">
                <span>{{ scope.row.id || '-' }}</span>
              </template>
            </el-table-column>
            
            <!-- 版本号 -->
            <el-table-column 
              prop="version" 
              label="版本号"
              min-width="120"
              sortable
            >
              <template #default="scope">
                <span class="version-text">{{ scope.row.version || '-' }}</span>
              </template>
            </el-table-column>
            
            <!-- 组织ID -->
            <el-table-column 
              prop="organization_id" 
              label="组织ID"
              min-width="150"
              sortable
            >
              <template #default="scope">
                <span>{{ scope.row.organization_id || '-' }}</span>
              </template>
            </el-table-column>
            
            <!-- 操作 (已注释) -->
            <!-- <el-table-column 
              label="操作"
              min-width="150"
              fixed="right"
            >
              <template #default="scope">
                <el-button 
                  type="primary" 
                  size="small" 
                  @click="handleEdit(scope.row)"
                  style="margin-right: 5px"
                >
                  编辑
                </el-button>
                <el-button 
                  type="danger" 
                  size="small" 
                  @click="handleDelete()"
                >
                  删除
                </el-button>
              </template>
            </el-table-column> -->
          </el-table>
          
          <!-- 分页 -->
          <div class="pagination-container">
            <el-pagination
              v-if="total > 0"
              background
              layout="total, sizes, prev, pager, next, jumper"
              :total="total"
              :page-sizes="[10, 20, 50, 100]"
              :current-page="currentPage"
              :page-size="pageSize"
              @size-change="handleSizeChange"
              @current-change="handlePageChange"
            />
          </div>
        </el-card>
      </div>

      <!-- 编辑对话框 -->
      <el-dialog
        v-model="editDialogVisible"
        title="编辑固件版本"
        width="600px"
        center
      >
        <el-form :model="editForm" :rules="editRules" ref="editFormRef" label-width="100px">
          <el-form-item label="版本号" prop="version">
            <el-input v-model="editForm.version" placeholder="请输入版本号" />
          </el-form-item>
          <el-form-item label="组织ID" prop="organization_id">
            <el-input v-model="editForm.organization_id" placeholder="请输入组织ID" />
          </el-form-item>
        </el-form>
        
        <template #footer>
          <div class="dialog-footer">
            <el-button @click="editDialogVisible = false">取消</el-button>
            <el-button type="primary" @click="handleSaveEdit" :loading="editLoading">保存</el-button>
          </div>
        </template>
      </el-dialog>

      <!-- 导入对话框 -->
      <el-dialog
        v-model="importDialogVisible"
        :title="$t('SerialNumberUpgrade.importTitle')"
        width="600px"
        center
      >
        <el-upload
          ref="uploadRef"
          :http-request="handleImportFileUpload"
          :file-list="importFileList"
          :on-remove="handleRemoveFile"
          :on-change="handleFileChange"
          :on-error="handleUploadError"
          :before-upload="beforeUpload"
          :auto-upload="false"
          :limit="1"
          accept=".xlsx,.xls,.csv"
          drag
        >
          <el-icon class="el-icon--upload"><Plus /></el-icon>
          <div class="el-upload__text">
            {{ $t('SerialNumberUpgrade.dragFileHereOrClickToUpload') }}
            <em>{{ $t('SerialNumberUpgrade.importFileTip') }}</em>
          </div>
        </el-upload>

        <template #footer>
          <div class="dialog-footer">
            <el-button @click="importDialogVisible = false">
              {{ $t('SerialNumberUpgrade.cancel') }}
            </el-button>
            <el-button
              type="primary"
              @click="submitImport"
              :disabled="!importFile || isUploading"
              :loading="importLoading || isUploading"
            >
              {{ $t('SerialNumberUpgrade.confirm') }}
            </el-button>
          </div>
        </template>
      </el-dialog>
    </div>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus';
import { Plus, Upload, Search, RefreshRight } from '@element-plus/icons-vue';
import { uploadFirmwareVersions, getFirmwareVersions } from '@/api/api';

export default {
  components: {
    Plus,
    Upload,
    Search,
    RefreshRight
  },
  data() {
    return {
      // 编辑相关
      editDialogVisible: false,
      editLoading: false,
      editFormRef: null,
      editForm: {
        id: null,
        version: '',
        organization_id: ''
      },
      editRules: {
        version: [
          { required: true, message: '请输入版本号', trigger: 'blur' }
        ],
        organization_id: [
          { required: true, message: '请输入组织ID', trigger: 'blur' }
        ]
      },
      
      // 导入相关
      importDialogVisible: false,
      importLoading: false,
      isUploading: false,
      importFile: null,
      importFileList: [],
      uploadRef: null,
      
      // 搜索相关
      searchForm: {
        version: '',
        organization_id: ''
      },
      
      // 表格相关
      firmwareList: [],
      tableLoading: false,
      currentPage: 1,
      pageSize: 10, // 内部使用，保持与Element Plus分页组件一致
      total: 0
    };
  },
  mounted() {
    // 页面加载时获取固件列表
    this.fetchFirmwareList();
  },
  methods: {
    // 获取固件列表
    async fetchFirmwareList() {
      try {
        this.tableLoading = true;
        
        // 构建参数对象，使用后端期望的参数名
        const params = {
          page: this.currentPage,
          per_page: this.pageSize // 使用per_page代替pageSize
        };
        
        // 只添加有值的搜索条件
        if (this.searchForm.version) {
          params["version-eq"] = this.searchForm.version;
        }
        
        if (this.searchForm.organization_id) {
          params["organization_id-eq"] = this.searchForm.organization_id;
        }
        
        const result = await getFirmwareVersions(params);
        
        // 验证API返回数据结构
        if (result) {
          // 处理Laravel分页响应结构
          if (result.data && Array.isArray(result.data) && result.meta) {
            // 从data字段获取固件列表
            this.firmwareList = result.data;
            
            // 从meta字段获取分页信息
            this.total = typeof result.meta.total === 'number' ? result.meta.total : result.data.length;
            
            // 更新分页信息
            if (result.meta.current_page) {
              this.currentPage = result.meta.current_page;
            }
            
            if (result.meta.per_page) {
              this.pageSize = result.meta.per_page;
            }
          } 
          // 处理包含code字段的响应结构
          else if (result.code === 0) {
            // 处理不同的数据结构
            if (Array.isArray(result.data)) {
              // 如果data直接是数组
              this.firmwareList = result.data;
              this.total = result.data.length;
            } else if (result.data && Array.isArray(result.data.data)) {
              // 如果数据在data.data中
              this.firmwareList = result.data.data;
              this.total = typeof result.data.total === 'number' ? result.data.total : result.data.data.length;
            } else if (result.data && Array.isArray(result.data.items)) {
              // 如果数据在data.items中
              this.firmwareList = result.data.items;
              this.total = typeof result.data.total === 'number' ? result.data.total : result.data.items.length;
            } else if (result.data && typeof result.data === 'object') {
              // 如果data是对象，可能是单条记录
              this.firmwareList = [result.data];
              this.total = 1;
            } else {
              // 其他情况
              this.firmwareList = [];
              this.total = 0;
            }
            
            // 更新分页信息
            if (result.data?.page) {
              this.currentPage = result.data.page;
            }
            
            // 后端可能返回per_page，转换为内部使用的pageSize
            if (result.data?.per_page) {
              this.pageSize = result.data.per_page;
            }
            
            // 兼容旧的pageSize字段
            if (result.data?.pageSize) {
              this.pageSize = result.data.pageSize;
            }
          } else {
            console.error('API返回错误:', result);
            ElMessage.error(result.msg || '获取固件列表失败');
            this.firmwareList = [];
            this.total = 0;
          }
        } else {
          console.error('API返回结果为空:', result);
          ElMessage.error('获取固件列表失败，返回结果为空');
          this.firmwareList = [];
          this.total = 0;
        }
      } catch (error) {
        // 分情况处理错误信息
        let errorMsg;
        if (error.response) {
          // 服务器返回了错误响应
          if (error.response.status === 401) {
            errorMsg = '认证失败，请重新登录';
          } else if (error.response.status === 403) {
            errorMsg = '没有权限访问该资源';
          } else if (error.response.status === 404) {
            errorMsg = '请求的资源不存在';
          } else if (error.response.status >= 500) {
            errorMsg = '服务器内部错误，请稍后重试';
          } else {
            errorMsg = error.response.data?.msg || error.response.data?.message || '获取固件列表失败';
          }
        } else if (error.request) {
          // 请求发送成功但没有收到响应
          errorMsg = '网络错误，服务器无响应';
        } else {
          // 请求配置错误
          errorMsg = error.message || '请求配置错误';
        }
        
        // 显示错误信息
        ElMessage.error(`获取固件列表失败: ${errorMsg}`);
      } finally {
        this.tableLoading = false;
      }
    },
    
    // 处理表格空数据事件
    handleEmpty() {
      // 可以在这里添加空数据时的处理逻辑
    },
    
    // 搜索
    handleSearch() {
      this.currentPage = 1;
      this.fetchFirmwareList();
    },
    
    // 重置搜索
    handleReset() {
      this.searchForm = {
        version: '',
        organization_id: ''
      };
      this.currentPage = 1;
      this.fetchFirmwareList();
    },
    
    // 页码变化
    handlePageChange(page) {
      this.currentPage = page;
      this.fetchFirmwareList();
    },
    
    // 每页条数变化
    handleSizeChange(size) {
      this.pageSize = size;
      this.currentPage = 1;
      this.fetchFirmwareList();
    },
    
    // 编辑
    handleEdit(row) {
      this.editForm = { ...row };
      this.editDialogVisible = true;
    },
    
    // 保存编辑
    async handleSaveEdit() {
      try {
        await this.$refs.editFormRef.validate();
        
        this.editLoading = true;
        
        // 这里应该调用API更新固件版本
        // 假设API是putFirmwareVersion(id, data)
        // 由于API还没有实现，先模拟成功
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        ElMessage.success('编辑成功');
        this.editDialogVisible = false;
        this.fetchFirmwareList(); // 刷新列表
      } catch (error) {
        if (error !== false) { // 表单验证失败时error是false
          ElMessage.error('编辑失败，请重试');
        }
      } finally {
        this.editLoading = false;
      }
    },
    
    // 删除
    handleDelete() {
      this.$confirm('确定要删除该固件版本吗？此操作不可恢复。', '删除确认', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          // 这里应该调用API删除固件版本
          // 由于API还没有实现，先模拟成功
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          ElMessage.success('删除成功');
          this.fetchFirmwareList(); // 刷新列表
        } catch (error) {
          ElMessage.error('删除失败，请重试');
        }
      }).catch(() => {
        ElMessage.info('已取消删除');
      });
    },
    
    // 状态变化
    handleStatusChange(row) {
      ElMessage.info(`固件版本 ${row.version} 状态已更新为: ${row.status ? '启用' : '禁用'}`);
      // 这里可以添加状态更新的API调用
    },
    
    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    // 格式化文件大小
    formatFileSize(bytes) {
      if (!bytes) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    // 处理导入
    handleImport() {
      this.importDialogVisible = true;
    },

    // 文件上传前验证
    beforeUpload(file) {
      const isExcel = file.type === 'application/vnd.ms-excel' || 
                     file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                     file.type === 'text/csv';
      const isLt2M = file.size / 1024 / 1024 < 2;

      if (!isExcel) {
        ElMessage.error('只支持上传 Excel (.xlsx, .xls) 和 CSV 文件');
        return false;
      }
      if (!isLt2M) {
        ElMessage.error('上传文件大小不能超过 2MB');
        return false;
      }
      return true;
    },

    // 处理文件选择变化
    handleFileChange(file, fileList) {
      // 检查文件类型是否有效
      if (file.status === 'ready' && this.beforeUpload(file.raw)) {
        this.importFile = file.raw;
        this.importFileList = fileList;
      } else if (file.status === 'ready') {
        // 如果文件类型无效，清空选择
        this.importFileList = [];
        this.importFile = null;
      }
    },

    // 处理上传错误
    handleUploadError() {
      ElMessage.error('文件上传失败，请重试');
    },

    // 处理导入文件上传
    handleImportFileUpload(options) {
      const { file } = options;
      this.isUploading = true;
      this.importFile = file;
      this.importFileList = [{ name: file.name, status: 'success' }];
      this.isUploading = false;
    },

    // 处理移除文件
    handleRemoveFile() {
      this.importFile = null;
      this.importFileList = [];
    },

    // 提交导入
    async submitImport() {
      try {
        if (!this.importFile) {
          ElMessage.warning(this.$t('SerialNumberUpgrade.selectFileFirst'));
          return;
        }

        // 验证文件扩展名
        const fileName = this.importFile.name;
        const fileExt = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();
        if (!['.xlsx', '.xls', '.csv'].includes(fileExt)) {
          ElMessage.error('只支持上传 Excel (.xlsx, .xls) 和 CSV 文件');
          return;
        }

        this.importLoading = true;

        // 构建importData对象，包含文件对象
        const importData = {
          file: this.importFile
        };

        // 获取导入结果
        const result = await uploadFirmwareVersions(importData);

        // 显示导入结果
        if (result.code === 0) {
          ElMessage.success('导入成功');
          // 导入成功后刷新数据
          this.fetchFirmwareList();
        } else {
          ElMessage.error(result.msg || '导入失败');
        }

        this.importDialogVisible = false;
        this.importFile = null;
        this.importFileList = [];
      } catch (error) {
        ElMessage.error('导入过程中发生错误，请检查文件格式和内容是否符合要求');
      } finally {
        this.importLoading = false;
      }
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/publicStyle.css";

.product-management-2023-page {
  width: 100%;
  min-height: 0;
  height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
}

.content-container {
  width: 100%;
  flex: 1;
  min-height: 0;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
}

/* 操作区域 */
.operation-area {
  margin-bottom: 16px;
  display: flex;
  justify-content: flex-start;
}

/* 搜索区域 */
.search-area {
  margin-bottom: 16px;
}

.search-card {
  background-color: #ffffff;
  border-radius: 4px;
}

.search-form {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

:deep(.el-form-item) {
  margin-bottom: 20px;
}

:deep(.el-input__inner),
:deep(.el-select__wrapper) {
  border-radius: 4px;
}

/* 表格区域 */
.table-area {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.table-card {
  background-color: #ffffff;
  border-radius: 4px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

:deep(.el-table) {
  flex: 1;
}

:deep(.el-table__header-wrapper th) {
  background-color: #f5f7fa;
  font-weight: bold;
}

.version-text {
  font-weight: 500;
  color: #409eff;
}

.description-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

/* 分页区域 */
.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 10px 0;
}

/* 导入对话框 */
.el-upload-dragger {
  width: 100%;
  height: 200px;
  margin-bottom: 20px;
}
</style>